import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';
import { Search, UserCheck, FileText, MessageSquare, Target, Clock, Shield, Users, Briefcase, TrendingUp, Award, ChevronRight, Check, X } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const Recruteur = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Configuration
  const seoConfig = {
    title: 'Recrutement Commercial & Talent Acquisition - FOMOW | Profils Sales B2B',
    description: 'Recrutement de commerciaux B2B performants : chasse de profils, assessment, onboarding. Trouvez les talents sales alignés avec votre culture et objectifs.',
    path: '/services/recruteur',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Acquisition de Talents Stratégiques",
      "description": "Recrutement et intégration de profils commerciaux performants",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Recruitment Services"
    }
  };

  // Process de sélection
  const selectionProcess = [
    { 
      num: '01',
      title: 'Définition du profil', 
      desc: 'Construction d\'une scorecard précise basée sur vos besoins réels, votre culture et vos objectifs de performance.',
      icon: FileText
    },
    { 
      num: '02',
      title: 'Sourcing actif', 
      desc: 'Chasse proactive des meilleurs talents via notre réseau, LinkedIn et canaux spécialisés.',
      icon: Search
    },
    { 
      num: '03',
      title: 'Évaluation structurée', 
      desc: 'Entretiens approfondis, mises en situation, roleplay sur cas réels et tests de compétences.',
      icon: MessageSquare
    },
    { 
      num: '04',
      title: 'Présentation ciblée', 
      desc: 'Shortlist qualifiée avec évaluation détaillée de chaque candidat : forces, axes de vigilance, recommandations.',
      icon: UserCheck
    },
  ];

  // Profils recrutés
  const profiles = [
    { 
      icon: Target, 
      title: 'SDR / BDR', 
      desc: 'Profils juniors pour la prospection et génération de leads qualifiés.',
      skills: ['Prospection', 'Qualification', 'Cold calling', 'Ténacité']
    },
    { 
      icon: Briefcase, 
      title: 'Account Executive', 
      desc: 'Commerciaux confirmés pour le closing et la négociation de deals.',
      skills: ['Closing', 'Négociation', 'Discovery', 'Vente de valeur']
    },
    { 
      icon: Users, 
      title: 'Account Manager / CSM', 
      desc: 'Experts en relation client, fidélisation et développement de comptes.',
      skills: ['Relation client', 'Upsell', 'Rétention', 'Empathie']
    },
    { 
      icon: TrendingUp, 
      title: 'Head of Sales / VP Sales', 
      desc: 'Leaders commerciaux pour structurer et piloter vos équipes revenue.',
      skills: ['Leadership', 'Stratégie', 'Coaching', 'Scaling']
    },
  ];

  // Ce qui nous différencie
  const differentiators = [
    {
      icon: Clock,
      title: 'Délais maîtrisés',
      desc: 'Premiers candidats présentés sous 10 jours. Closing moyen en 45 jours.',
    },
    {
      icon: Shield,
      title: 'Garantie remplacement',
      desc: 'Remplacement gratuit si la période d\'essai n\'est pas validée.',
    },
    {
      icon: Award,
      title: 'Expertise métier',
      desc: '10+ ans d\'expérience dans le recrutement de profils revenue en contexte de croissance.',
    },
    {
      icon: Search,
      title: 'Transparence totale',
      desc: 'Visibilité complète sur le pipeline, reporting régulier, communication directe.',
    },
  ];

  // Notre approche vs cabinet classique
  const comparisons = [
    { bad: 'CV matching sur mots-clés', good: 'Évaluation approfondie des compétences et du potentiel' },
    { bad: 'Processus opaque et long', good: 'Transparence totale, premiers candidats en 10 jours' },
    { bad: 'Recruteurs généralistes', good: 'Experts dédiés aux fonctions revenue' },
    { bad: 'Peu de suivi post-recrutement', good: 'Accompagnement jusqu\'à validation de la période d\'essai' },
  ];

  // Tarification
  const pricing = [
    {
      title: 'Postes Experts',
      subtitle: 'SDR, BDR, AE, AM, CSM',
      fee: '18%',
      basis: 'du package annuel',
      details: ['100% au closing', 'Garantie remplacement 3 mois', 'Exclusivité 6 semaines recommandée'],
    },
    {
      title: 'Postes Leadership',
      subtitle: 'Head of Sales, VP Sales, CRO',
      fee: '22%',
      basis: 'du package annuel',
      details: ['75% au closing', '25% à validation période d\'essai', 'Garantie remplacement 6 mois'],
      featured: true,
    },
  ];

  // FAQ
  const faqs = [
    { 
      q: 'Comment qualifiez-vous les candidats ?', 
      a: 'Notre process d\'évaluation inclut des entretiens structurés, des mises en situation sur cas réels, des roleplay de vente et des prises de références systématiques. On évalue les compétences techniques mais aussi le fit culturel et le potentiel.' 
    },
    { 
      q: 'Quelle garantie proposez-vous ?', 
      a: 'Garantie de remplacement gratuit si le candidat ne valide pas sa période d\'essai (3 mois pour les postes experts, 6 mois pour le leadership). C\'est notre engagement qualité.' 
    },
    { 
      q: 'Combien de temps pour un recrutement ?', 
      a: 'Premiers candidats présentés sous 10 jours. Closing moyen en 45 jours entre le brief initial et la signature. On peut accélérer selon l\'urgence et la disponibilité côté client.' 
    },
    { 
      q: 'Travaillez-vous en exclusivité ?', 
      a: 'On recommande une exclusivité de 6 semaines pour optimiser le processus et garantir un engagement mutuel. On peut aussi travailler en parallèle de vos recruteurs internes ou du referral.' 
    },
    { 
      q: 'Quels secteurs couvrez-vous ?', 
      a: 'Notre expertise couvre principalement la Tech (SaaS, B2B), les scale-ups et les entreprises en transformation digitale. Nous intervenons aussi pour des PME et ETI ambitieuses.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">Recrutement</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Acquisition de Talents Stratégiques
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed mb-4">
              Constituez des équipes revenue <span className="text-[#2A41E5] font-semibold">performantes et durables</span>. 
              Nous identifions et sélectionnons les talents qui feront la différence.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-lg text-white/60 max-w-2xl mb-10">
              Une expertise métier pointue, un process rigoureux et une garantie de résultat. 
              Parce qu'un mauvais recrutement coûte plus cher que le bon.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Contactez-nous
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Ce qui nous différencie */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos engagements</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Des recruteurs dédiés aux fonctions revenue
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Notre expertise est concentrée sur les métiers commerciaux. C'est ce qui fait la différence.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((diff, idx) => {
              const IconComponent = diff.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="bg-[#F5F5F5] rounded-2xl p-6 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300 text-center">
                    <div className="w-12 h-12 rounded-xl bg-[#2A41E5]/10 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-[#2A41E5]" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{diff.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{diff.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notre process */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Notre méthode</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 text-center">
              Un process de sélection rigoureux
            </h2>
            <p className="text-lg text-white/60 text-center max-w-3xl mx-auto mb-16">
              Chaque étape est conçue pour identifier les meilleurs profils et minimiser les erreurs de casting.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {selectionProcess.map((step, idx) => {
              const IconComponent = step.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-[#2A41E5]/50 transition-all duration-300 group h-full">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 rounded-xl bg-[#2A41E5] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-white font-bold text-lg">{step.num}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <IconComponent className="w-5 h-5 text-[#2A41E5]" />
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        </div>
                        <p className="text-white/70 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Profils recrutés */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos expertises</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Les profils que nous recrutons
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Du junior au leadership, nous couvrons l'ensemble des fonctions revenue.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {profiles.map((profile, idx) => {
              const IconComponent = profile.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-start gap-4 mb-4">
                      <IconComponent className="w-6 h-6 text-[#2A41E5] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{profile.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{profile.desc}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200">
                      {profile.skills.map((skill, i) => (
                        <span key={i} className="px-3 py-1 bg-[#2A41E5]/10 text-[#2A41E5] text-sm font-medium rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ce qu'on fait vs cabinet classique */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Pas un cabinet de recrutement classique
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Notre spécialisation et notre process font la différence en termes de qualité et de rapidité.
            </p>
          </AnimatedSection>
          
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2">
              {/* Ce qu'on NE fait PAS */}
              <div className="p-8 md:p-10 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2">
                  <X className="w-6 h-6 text-red-400" />
                  Cabinet généraliste
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-gray-500">
                        <span className="text-red-400 mt-1">✗</span>
                        {comp.bad}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
              
              {/* Ce qu'on FAIT */}
              <div className="p-8 md:p-10 bg-[#041C2C]">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Notre approche
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-white/80">
                        <span className="text-green-400 mt-1">✓</span>
                        {comp.good}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tarification */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Tarification</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              100% basé sur le succès
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Pas de frais de lancement. Vous ne payez qu'au recrutement effectif.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {pricing.map((plan, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className={`rounded-2xl p-8 h-full border transition-all duration-300 ${
                  plan.featured 
                    ? 'bg-[#041C2C] border-[#2A41E5] shadow-xl' 
                    : 'bg-[#F5F5F5] border-gray-100 hover:border-[#2A41E5]/30'
                }`}>
                  {plan.featured && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#2A41E5] text-white text-xs font-medium mb-4">
                      Recommandé
                    </div>
                  )}
                  <h3 className={`text-xl font-bold mb-1 ${plan.featured ? 'text-white' : 'text-gray-900'}`}>
                    {plan.title}
                  </h3>
                  <p className={`text-sm mb-6 ${plan.featured ? 'text-white/60' : 'text-gray-500'}`}>
                    {plan.subtitle}
                  </p>
                  
                  <div className="mb-6">
                    <span className={`text-5xl font-bold ${plan.featured ? 'text-[#2A41E5]' : 'text-[#2A41E5]'}`}>
                      {plan.fee}
                    </span>
                    <span className={`text-lg ml-2 ${plan.featured ? 'text-white/60' : 'text-gray-500'}`}>
                      {plan.basis}
                    </span>
                  </div>
                  
                  <ul className="space-y-3">
                    {plan.details.map((detail, i) => (
                      <li key={i} className={`flex items-center gap-3 text-sm ${plan.featured ? 'text-white/80' : 'text-gray-600'}`}>
                        <Check className={`w-4 h-4 flex-shrink-0 ${plan.featured ? 'text-green-400' : 'text-green-500'}`} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#2A41E5]/30 transition-colors">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <svg 
                      className={`w-5 h-5 text-[#2A41E5] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à renforcer vos équipes revenue ?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Discutons de vos besoins en recrutement. On vous présente nos profils en cours et notre approche.<br />
              Sans engagement.
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Réserver un échange
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/coach" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Coaching & Excellence Commerciale</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Formation et coaching pour développer les compétences de vos équipes commerciales.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/pilote" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Direction Commerciale Externalisée</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Des leaders commerciaux intégrés à votre équipe pour exécuter et piloter.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Recruteur;
